import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Input, Textarea } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'

/*
 * Admin — application settings (GET /app-settings). Super-admin only. Settings
 * are grouped by category; each value is edited with a type-aware editor
 * (boolean / integer / json / string) and saved via PUT /app-settings/:id.
 * Edit-only: the 28 settings are seeded by the backend, so the admin UI doesn't
 * create or delete them.
 */
const CAT_ICON = { archive: 'invoice', billing: 'credit-card', email: 'mail', netfly: 'link', peppol: 'send', security: 'shield', stripe: 'credit-card' }

export default function AppSettingsTab() {
  const { t } = useTranslation()
  const { toast, notify } = useFeedback()
  const [settings, setSettings] = useState([])
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState({}) // id -> edited value
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/app-settings').then(({ data }) => { setSettings(data?.settings || []); setDrafts({}) }).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const dirtyIds = Object.keys(drafts)
  // Single general save: persist every changed setting at once.
  const saveAll = async () => {
    if (!dirtyIds.length) return
    setSaving(true)
    try {
      await Promise.all(dirtyIds.map((id) => api.put(`/app-settings/${id}`, { value: String(drafts[id]) })))
      toast(t('admin.appSettings.saved'), 'success'); load()
    } catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) } finally { setSaving(false) }
  }
  const reset = () => setDrafts({})

  if (loading) return <div style={{ color: 'var(--color-text-muted)', padding: 16 }}>{t('common.loading')}</div>

  const byCat = {}
  for (const s of settings) (byCat[s.category || 'other'] ??= []).push(s)
  const cats = Object.keys(byCat).sort()

  return (
    <>
      {/* Single general save bar (sticky) — replaces per-setting save buttons. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', marginBottom: 18, background: dirtyIds.length ? 'var(--color-bg-lilac-strong)' : '#fff', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: 'var(--shadow-card)' }}>
        <span style={{ fontSize: 13.5, color: 'var(--color-text)', fontWeight: 600 }}>
          {dirtyIds.length ? t('admin.appSettings.pending', { count: dirtyIds.length }) : t('admin.appSettings.noChanges')}
        </span>
        <div style={{ display: 'flex', gap: 10 }}>
          {dirtyIds.length > 0 && <Button variant="secondary" onClick={reset} disabled={saving}>{t('common.cancel')}</Button>}
          <Button variant="primary" icon="check" loading={saving} disabled={!dirtyIds.length} onClick={saveAll}>{t('common.save')}</Button>
        </div>
      </div>
      {cats.map((cat) => (
        <div key={cat} style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ color: 'var(--color-primary)' }}><Icon name={CAT_ICON[cat] || 'settings'} /></span>
            <h2 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-ink)', textTransform: 'capitalize' }}>{t(`admin.appSettings.categories.${cat}`, cat)}</h2>
          </div>
          <div style={{ background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
            {byCat[cat].map((s, i) => {
              const dirty = Object.prototype.hasOwnProperty.call(drafts, s.id)
              const value = dirty ? drafts[s.id] : s.value
              const setVal = (v) => setDrafts((d) => ({ ...d, [s.id]: v }))
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 18px', borderTop: i ? '1px solid var(--color-border-row)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-ink)' }}>{s.name}</code>
                      <Badge tone="neutral">{s.value_type}</Badge>
                    </div>
                    {s.description && <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 3 }}>{s.description}</p>}
                  </div>
                  {dirty && <span title={t('admin.appSettings.modified')} style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)', marginTop: 14 }} />}
                  <div style={{ width: 280, flexShrink: 0 }}>
                    <ValueEditor type={s.value_type} value={value} onChange={setVal} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </>
  )
}

function ValueEditor({ type, value, onChange }) {
  if (type === 'boolean') {
    const on = value === true || value === 'true'
    return (
      <button type="button" onClick={() => onChange(on ? 'false' : 'true')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 14px', borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-strong)', background: '#fff', cursor: 'pointer', width: '100%' }}>
        <span style={{ width: 38, height: 20, borderRadius: 999, background: on ? 'var(--color-primary)' : 'var(--color-border-strong)', position: 'relative', transition: 'background .15s' }}>
          <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--color-ink)' }}>{on ? 'true' : 'false'}</span>
      </button>
    )
  }
  if (type === 'json') return <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12.5 }} />
  if (type === 'integer' || type === 'number') return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
  return <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
}
